import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { AlertCircle, Check, Info } from 'lucide-react'

import type { VariantProps } from '@config/tailwind/cva'
import cn from '@config/tailwind/cn'
import { cva } from '@config/tailwind/cva'

import { typographyVariants } from '../lib/typography'

const inlineNoticeVariants = cva('ui:flex ui:w-full ui:items-center ui:gap-3 ui:rounded-lg ui:border ui:px-4 ui:py-3', {
  variants: {
    variant: {
      default: 'ui:bg-navy-20 ui:border-navy-80 ui:text-navy-100',
      success: 'ui:bg-blue-20 ui:border-blue-50 ui:text-navy-100',
      error: 'ui:bg-error-container ui:border-error ui:text-error',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const defaultIcons: Record<NonNullable<VariantProps<typeof inlineNoticeVariants>['variant']>, React.ReactNode> = {
  default: <Info className='ui:size-5 ui:shrink-0' aria-hidden />,
  success: <Check className='ui:size-5 ui:shrink-0' aria-hidden />,
  error: <AlertCircle className='ui:size-5 ui:shrink-0' aria-hidden />,
}

export interface InlineNoticeProps extends React.ComponentProps<'div'>, VariantProps<typeof inlineNoticeVariants> {
  /**
   * Replaces the default icon for this variant.
   */
  icon?: React.ReactNode
  /**
   * Right-side control (e.g. `InlineNoticeAction` with `asChild` + anchor, or a plain button).
   */
  action?: React.ReactNode
}

function InlineNotice({ className, variant = 'default', icon, action, children, ...props }: InlineNoticeProps) {
  const resolvedVariant = variant ?? 'default'

  return (
    <div
      data-slot='inline-notice'
      role={resolvedVariant === 'error' ? 'alert' : 'status'}
      className={cn(inlineNoticeVariants({ variant: resolvedVariant }), className)}
      {...props}
    >
      <span className='ui:flex ui:shrink-0 ui:items-center ui:justify-center ui:text-current'>
        {icon ?? defaultIcons[resolvedVariant]}
      </span>
      <div className={cn(typographyVariants({ variant: 'body-md' }), 'ui:min-w-0 ui:flex-1')}>{children}</div>
      {action ? <div className='ui:flex ui:shrink-0 ui:items-center'>{action}</div> : null}
    </div>
  )
}

export interface InlineNoticeActionProps extends React.ComponentProps<'button'> {
  asChild?: boolean
}

function InlineNoticeAction({ className, asChild = false, type, ...props }: InlineNoticeActionProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      type={asChild ? undefined : (type ?? 'button')}
      data-slot='inline-notice-action'
      className={cn(
        typographyVariants({ variant: 'label-sm' }),
        'ui:inline-flex ui:items-center ui:justify-center ui:rounded-sm ui:border-none ui:bg-transparent ui:p-0 ui:text-inherit ui:underline ui:underline-offset-2 ui:focus-visible:outline-none ui:focus-visible:ring-2 ui:focus-visible:ring-outline ui:focus-visible:ring-offset-2 ui:focus-visible:ring-offset-neutral-white',
        className,
      )}
      {...props}
    />
  )
}

export { InlineNotice, InlineNoticeAction, inlineNoticeVariants }
